import classNames from 'classnames'
import { memo, useRef, type ReactNode } from 'react'
import { Tooltip } from '@opallabs/lapidary'
import {
  useDayDropState,
  useDayDropTarget,
  type CalendarDropProposal,
} from '../../dnd'
import {
  buildDayRows,
  runStartRows,
  type DayBudget,
  type PlacedBar,
  type PlacedSpan,
} from '../../laneLayout'
import { type CalendarMenuProps } from '../../menu'
import { type CalendarDay } from '../../monthGrid'
import { type CalendarItem, type ResolveDisplay } from '../../types'
import { ContentChip } from '../ContentChip'
import { spanPositionStyle } from '../inlineStyles'
import { TOOLTIP_DELAY, useItemTooltip } from '../ItemTooltip'
import { MomentBar } from '../MomentBar'
import { notShownSummary } from '../notShownSummary'
import * as styles from '../styles'
import { type CalendarDensity, type CalendarNamePriority } from '../types'
import { weekBreakLabel } from '../weekBreak'

const EMPTY_RUN_STARTS: ReadonlySet<string> = new Set()

export type DayCellProps = Readonly<{
  /** Bars that start on this day. Bars that only pass over it live in their own cell. */
  bars: readonly PlacedBar[]
  /** How many rows the day can draw, counted in whatever it is stacking. */
  budget: DayBudget
  chips: readonly CalendarItem[]
  colorFor: (item: CalendarItem) => string | undefined
  day: CalendarDay
  /** Reads the full date for assistive technology. */
  dateLabel: string
  dayNumber: string
  /** Summary of the day, drawn when a person rests on its number. */
  dayTooltip?: (date: Date) => ReactNode
  density?: CalendarDensity
  /** Reads a run of days, or one day, the way the rest of the calendar does. */
  formatDateRange: (start: Date, end: Date) => string
  /**
   * The zone a time is read in, for the tooltip. A time on the grid is not
   * marked: it would be the same abbreviation on every chip of the month.
   */
  formatTimeZone?: (instant: string) => string | undefined
  /**
   * What this day holds that the grid could not draw at all: the moments whose
   * lane the week row had no room for, and the spans another day they cover
   * had no room for. The day counts them behind its own "+N more".
   */
  hiddenItems: readonly CalendarItem[]
  /**
   * The item the pointer or the keyboard is on, anywhere in the month. Every
   * piece of it is marked, so a moment or a window that crosses a week row
   * reads as the one thing it is.
   */
  hoveredItemId?: string
  /** The item whose panel is open. Every piece of it is marked. */
  openItemId?: string
  /** Pushes an item back when quickfind did not match it. */
  isDimmed: (item: CalendarItem) => boolean
  isFocused: boolean
  /** The pointer is over this day. The grid works out which day that is. */
  isHovered: boolean
  /**
   * The mark every chip leads with. The month hands it down, because which
   * mark says the most about a chip is a question of what the month is sorted
   * by.
   */
  mark?: 'account' | 'channel'
  /** The right-click menu for bars and chips. Absent means they have none. */
  menu?: CalendarMenuProps
  /** Which of a content item's two names it leads with. */
  namePriority?: CalendarNamePriority
  onDayOpen?: (date: Date) => void
  onItemClick?: (item: CalendarItem) => void
  onItemDrop?: (proposal: CalendarDropProposal) => void
  /** Says which item the pointer or the keyboard has arrived on, or none. */
  onItemHover?: (itemId: string | undefined) => void
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>
  onOverflowClick?: (date: Date) => void
  /** The moment a piece of content belongs to, when it is on screen. */
  parentOf: (item: CalendarItem) => CalendarItem | undefined
  renderChannelIcon?: (item: CalendarItem, variant?: 'card') => ReactNode
  renderDayActions?: (date: Date) => ReactNode
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
  /** Rows of the chip column a span is drawn over, which hold no chip. */
  reservedRows: readonly number[]
  resolveDisplay?: ResolveDisplay
  /**
   * The chips of `chips` that open a run of their own. Each of them is drawn a
   * `runGap` lower, which is what divides one run from the one above it.
   */
  runStartIds?: ReadonlySet<string>
  shouldShowContentName?: boolean
  shouldShowLabelColor?: boolean
  shouldShowMomentBadge?: boolean
  shouldShowTime?: boolean
  /**
   * Placeholder chips to draw while the day is waiting for its first answer.
   * The grid decides how many, so the scatter across the month is its own.
   */
  skeletonChips?: number
  /** Spans that start on this day, drawn across the days they cover. */
  spans: readonly PlacedSpan[]
}>

const DayCellComponent: React.FC<DayCellProps> = ({
  bars,
  budget,
  chips,
  colorFor,
  dateLabel,
  day,
  dayNumber,
  dayTooltip,
  density,
  formatDateRange,
  formatTimeZone,
  hiddenItems,
  hoveredItemId,
  isDimmed,
  isFocused,
  isHovered,
  mark,
  menu,
  namePriority,
  onDayOpen,
  onItemClick,
  onItemDrop,
  onItemHover,
  onKeyDown,
  onOverflowClick,
  openItemId,
  parentOf,
  renderChannelIcon,
  renderDayActions,
  renderStatusIcon,
  reservedRows,
  resolveDisplay,
  runStartIds = EMPTY_RUN_STARTS,
  shouldShowContentName,
  shouldShowLabelColor,
  shouldShowMomentBadge,
  shouldShowTime,
  skeletonChips = 0,
  spans,
}) => {
  const cellRef = useRef<HTMLDivElement>(null)
  const isDragEnabled = onItemDrop !== undefined
  const dropState = useDayDropState(day.key)
  const itemTooltips = useItemTooltip()

  useDayDropTarget({
    day,
    isEnabled: isDragEnabled,
    onDrop: onItemDrop,
    ref: cellRef,
  })

  // The rows a span took hold no chip of their own, so what the budget draws is
  // the column with those rows already left empty.
  const chipRows = buildDayRows(reservedRows, chips)
  const runStarts = runStartRows(chipRows, { reservedRows, runStartIds })

  const notShownItems = [
    ...hiddenItems,
    ...chipRows
      .slice(budget.visibleCount)
      .flatMap((item) => (item === null ? [] : [item])),
  ]

  const accountNameOf = (item: CalendarItem) =>
    item.accountId === undefined
      ? undefined
      : resolveDisplay?.('account', item.accountId)

  /** The day and the time a chip stands for, when it stands for one day. */
  const chipDays = (item: CalendarItem) => {
    const date = formatDateRange(day.date, day.date)
    if (item.time === undefined) return date

    const zone =
      item.startsAt === undefined ? undefined : formatTimeZone?.(item.startsAt)
    return `${date}, ${item.time}${zone === undefined ? '' : ` ${zone}`}`
  }

  // What a chip is, spelled out: a crowded row truncates its names, and a
  // window's width is a date range nobody can read off the grid.
  const itemTooltip = (item: CalendarItem, days: string) => {
    const parent = parentOf(item)
    const publisher =
      accountNameOf(item) ??
      (item.channelId === undefined
        ? undefined
        : resolveDisplay?.('channel', item.channelId))

    return (
      <span css={styles.itemTooltip}>
        <span>{item.title}</span>
        {parent && <span>{parent.title}</span>}
        {publisher && <span>{publisher}</span>}
        <span>{days}</span>
      </span>
    )
  }

  const renderChip = (item: CalendarItem, row: number) => (
    <ContentChip
      key={item.id}
      {...itemTooltips.triggerProps(item.id, () =>
        itemTooltip(item, chipDays(item))
      )}
      accountName={accountNameOf(item)}
      className={classNames({
        [styles.stateVariables.open]: openItemId === item.id,
        [styles.stateVariables.runStart]: runStarts.has(row),
      })}
      color={colorFor(item)}
      density={density}
      isDimmed={isDimmed(item)}
      isDraggable={isDragEnabled}
      item={item}
      mark={mark}
      menu={menu}
      namePriority={namePriority}
      parent={parentOf(item)}
      renderChannelIcon={renderChannelIcon}
      renderStatusIcon={renderStatusIcon}
      shouldShowContentName={shouldShowContentName}
      shouldShowLabelColor={shouldShowLabelColor}
      shouldShowMomentBadge={shouldShowMomentBadge}
      shouldShowTime={shouldShowTime}
      onItemClick={onItemClick}
      onItemHover={onItemHover}
    />
  )

  /** The row a span was drawn over, held open so the chips under it line up. */
  const renderSlot = (key: number) => (
    <div
      key={`slot-${key}`}
      aria-hidden={true}
      className={styles.selectors.chipSlot}
    />
  )

  const dayNumberButton = (
    <button
      aria-label={dateLabel}
      className={classNames(styles.selectors.dayNumber, {
        [styles.stateVariables.today]: day.isToday,
      })}
      type="button"
      onClick={() => onDayOpen?.(day.date)}
    >
      {dayNumber}
    </button>
  )

  return (
    <div
      ref={cellRef}
      aria-label={dateLabel}
      className={classNames(styles.selectors.cell, {
        [styles.stateVariables.dropForbidden]: dropState === 'before-moment',
        [styles.stateVariables.dropTarget]: dropState === 'allowed',
        [styles.stateVariables.hovered]: isHovered,
        [styles.stateVariables.otherMonth]: !day.isCurrentMonth,
      })}
      data-date={day.key}
      role="gridcell"
      tabIndex={isFocused ? 0 : -1}
      onKeyDown={onKeyDown}
    >
      <div className={styles.selectors.dayHeader}>
        {dayTooltip ? (
          <Tooltip
            content={<>{dayTooltip(day.date)}</>}
            delay={TOOLTIP_DELAY}
            side="top"
          >
            {dayNumberButton}
          </Tooltip>
        ) : (
          dayNumberButton
        )}
        {renderDayActions && (
          <span className={styles.selectors.dayActions}>
            {renderDayActions(day.date)}
          </span>
        )}
      </div>

      {bars.map((bar) => (
        <MomentBar
          key={bar.key}
          bar={bar}
          color={colorFor(bar.item)}
          isDimmed={isDimmed(bar.item)}
          isDraggable={isDragEnabled && bar.isStart}
          isHovered={hoveredItemId === bar.item.id}
          isOpen={openItemId === bar.item.id}
          menu={menu}
          renderStatusIcon={renderStatusIcon}
          onClick={onItemClick}
          onHoverChange={(isOn) =>
            onItemHover?.(isOn ? bar.item.id : undefined)
          }
        />
      ))}

      {spans.map((span) => (
        <span
          key={span.key}
          className={classNames(styles.selectors.span, {
            [styles.stateVariables.clippedEnd]: !span.isEnd,
            [styles.stateVariables.clippedStart]: !span.isStart,
          })}
          style={spanPositionStyle(span)}
          // The chip inside carries its own pointer and focus handlers, for
          // the tooltip, so the wrapper listens for both instead.
          onBlur={() => onItemHover?.(undefined)}
          onFocus={() => onItemHover?.(span.item.id)}
          onPointerEnter={() => onItemHover?.(span.item.id)}
          onPointerLeave={() => onItemHover?.(undefined)}
        >
          <ContentChip
            {...itemTooltips.triggerProps(span.item.id, () =>
              itemTooltip(span.item, formatDateRange(span.start, span.end))
            )}
            accountName={accountNameOf(span.item)}
            // The days the window runs are said in the name as well as in
            // the tooltip, and so is either edge it carries past this week.
            ariaLabel={`${span.item.title} in market, ${formatDateRange(
              span.start,
              span.end
            )}${weekBreakLabel({ isEnd: span.isEnd, isStart: span.isStart })}`}
            className={classNames({
              [styles.stateVariables.hovered]: hoveredItemId === span.item.id,
              [styles.stateVariables.open]: openItemId === span.item.id,
            })}
            color={colorFor(span.item)}
            density={density}
            edges={{ isEnd: span.isEnd, isStart: span.isStart }}
            isDimmed={isDimmed(span.item)}
            // A window is picked up on the week row it really starts in, the
            // way a moment's bar is: the other rows are the same window
            // carrying on, and dragging one of those would say the window
            // starts where it does not.
            isDraggable={isDragEnabled && span.isStart}
            isWide={span.span > 1}
            item={span.item}
            mark={mark}
            menu={menu}
            namePriority={namePriority}
            parent={parentOf(span.item)}
            renderChannelIcon={renderChannelIcon}
            renderStatusIcon={renderStatusIcon}
            shouldShowContentName={shouldShowContentName}
            shouldShowLabelColor={shouldShowLabelColor}
            shouldShowMomentBadge={shouldShowMomentBadge}
            shouldShowTime={shouldShowTime}
            onItemClick={onItemClick}
          />
        </span>
      ))}

      <div className={styles.selectors.chipColumn}>
        {Array.from({ length: skeletonChips }, (_unused, index) => (
          <div
            key={index}
            aria-hidden={true}
            className={styles.selectors.skeletonChip}
          />
        ))}
        {chipRows
          .slice(0, budget.visibleCount)
          .map((item, index) =>
            item === null ? renderSlot(index) : renderChip(item, index)
          )}
        {notShownItems.length > 0 && (
          <Tooltip
            align="start"
            content={notShownSummary(notShownItems)}
            delay={TOOLTIP_DELAY}
            side="bottom"
          >
            <button
              className={styles.selectors.overflow}
              type="button"
              onClick={() => onOverflowClick?.(day.date)}
            >
              {`+${notShownItems.length} more`}
            </button>
          </Tooltip>
        )}
      </div>

      {itemTooltips.tooltip}
    </div>
  )
}

/**
 * Drawn again only when its own props change. The grid draws again on every
 * hover, and a busy month holds thousands of chips, so a day that holds no
 * piece of the hovered item must be handed props that have not changed.
 */
export const DayCell = memo(DayCellComponent)
DayCell.displayName = 'Calendar: DayCell'
